/* eslint-disable react/prop-types */
import { ChangeEvent, FC, FormEvent } from 'react';
import { Card, Form } from 'react-bootstrap';
import WarningIcon from '@mui/icons-material/Warning';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Link from 'components/common/Link/Link';
import { ScrubbingRuleFormData, ScrubbingRuleFormVariant } from 'components/logProcessing/types';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';

import 'components/logProcessing/ScrubbingRuleForm.scss';

interface Props {
  data: ScrubbingRuleFormData;
  isWriteDisabled: boolean;
  validated: boolean;
  saveDisabled: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onReset: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  scrubbingfilterRules: JSX.Element;
}

const ScrubbingRuleForm: FC<Props> = ({
  data,
  isWriteDisabled,
  validated,
  saveDisabled,
  onChange,
  onReset,
  onSubmit,
  scrubbingfilterRules,
}) => (
  <Card>
    <Card.Body>
      <Form noValidate validated={validated} onSubmit={onSubmit} autoComplete="off">
        <Form.Group controlId="ruleName">
          <Form.Label>Name</Form.Label>
          <Form.Control
            required
            type="text"
            name="name"
            value={data.values.name}
            disabled={isWriteDisabled}
            placeholder="Descriptive name, like API Keys"
            onChange={onChange}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="ruleLogFilter">
          <Form.Label className="NewFilterForm__LabelWithAction">
            Source/Logfile/Logline Filter
            <Link href={`/events?filter=${data.values.logFilter}`} className="link" openInNewTab>
              Test filter &gt;&gt;
            </Link>
          </Form.Label>
          <Form.Control
            required
            as="textarea"
            name="logFilter"
            value={data.values.logFilter}
            disabled={isWriteDisabled}
            placeholder="Filter text, like $serverHost contains 'API' AND message contains 'KEY'"
            onChange={onChange}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
          <div className="NewFilterForm__WarningMessage">
            <ElementWithTooltip tooltip={scrubbingfilterRules}>
              <WarningIcon fontSize="small" className="NewFilterForm__WarningMessageIcon" />
            </ElementWithTooltip>
            Can't contain parsed fields. See details below, in About Scrubbing.
          </div>
        </Form.Group>
        <Form.Group controlId="ruleMatchText">
          <Form.Label>Match Text</Form.Label>
          <Form.Control
            required
            type="text"
            name="matchText"
            value={data.values.matchText}
            disabled={isWriteDisabled}
            placeholder="Regex expression"
            onChange={onChange}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="ruleReplaceWith">
          <Form.Label>Replace With</Form.Label>
          <Form.Control
            type="text"
            name="replaceWith"
            value={data.values.replaceWith}
            disabled={isWriteDisabled}
            placeholder="Text (or empty to remove)"
            onChange={onChange}
          />
        </Form.Group>
        <Form.Group className="NewFilterForm__Actions">
          {isWriteDisabled ? (
            <Button variant={ButtonVariant.PRIMARY} onClick={onReset}>
              OK
            </Button>
          ) : (
            <>
              <Button variant={ButtonVariant.PRIMARY} disabled={saveDisabled} type="submit">
                Save
              </Button>
              <Button variant={ButtonVariant.SECONDARY} onClick={onReset}>
                {data.variant === ScrubbingRuleFormVariant.ADD ? 'Clear' : 'Cancel'}
              </Button>
            </>
          )}
        </Form.Group>
      </Form>
    </Card.Body>
  </Card>
);

export default ScrubbingRuleForm;
